import { Button as AntButton, Descriptions, Modal, Select, Table, TableColumnsType } from "antd";
import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { createSemesterTestPaper, getTestPaperItems } from "../../../../api/semesterTest.api";
import { getTestTypes } from "../../../../api/testCenter.api";
import useAxios from "../../../../hooks/useAxios";
import useNotification from "../../../../hooks/useNotification";
import { getCommonCodeLabel } from "../../../../lib/store";
import { IGetTestPaperItems, ITestPaperItem } from "../../../../models/semesterTest.model";
import { IGetTestTypes } from "../../../../models/testCenter.model";
import { RootState } from "../../../../store/rootReducer";
import Button from "../../../Button";
import ErrorResponse from "../../../ErrorResponse";
import Loader from "../../../Loader";
import Radio from "../../../Radio";
import { getReverseYears } from "../../../../lib/util";
import TableEmpty from "../../../common/TableEmpty";

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  scheduleCode: number|undefined;
  doGetSemesterTestPapers: () => void;
  eduStepCode: string|undefined;
}

const SelectPaperModal: FC<IProps> = ({
  visible,
  setVisible,
  scheduleCode,
  doGetSemesterTestPapers,
  eduStepCode,
}) => {
  const { t } = useTranslation();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // 연도
  const SY0005 = commonCodes.find(item => item.commonCode === 'SY0005')?.detailCode;
  // 시험 회차
  const SY0011 = commonCodes.find(item => item.commonCode === 'SY0011')?.detailCode;
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetTestTypes>();
  const { data: res2, isLoading: isLoading2, error: error2, execute: execute2 } = useAxios<IGetTestPaperItems>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();

  const [examDiv, setExamDiv] = useState<string>();
  const [year, setYear] = useState<string>();
  const [testSeq, setTestSeq] = useState<string>();
  const [examTypeName, setExamTypeName] = useState<string>();
  const [examName, setExamName] = useState('');
  const [examCode, setExamCode] = useState<number>();

  useEffect(() => {
    execute(getTestTypes());
  }, []);

  useEffect(() => {
    setExamTypeName(undefined);
  }, [examDiv]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      doGetSemesterTestPapers();
      successNotification(t('common.message.create1'));
      setVisible(false);
    }
  }, [submitRes, submitIsLoading]);

  const onClose = () => {
    setExamDiv(undefined);
    setYear(undefined);
    setTestSeq(undefined);
    setExamTypeName(undefined);
    setExamName('');
    setExamCode(undefined);
    setVisible(false);
  }

  const onSearch = () => {
    const params: any = { openYn: 'Y' };
    if (examDiv) params.examDiv = examDiv;
    if (year) params.year = year;
    if (testSeq) params.testSeq = testSeq;
    if (examTypeName) params.examTypeName = examTypeName;
    if (examName) params.examName = examName;

    execute2(getTestPaperItems(params));
  }

  const onSubmit = () => {
    if (!scheduleCode) return;
    if (!examCode) {
      return infoNotification(t('program.semester.message27'));
    }

    submitExecute(createSemesterTestPaper(scheduleCode, { examCode }));
  }

  const columns: TableColumnsType<ITestPaperItem> = [
    {
      title: '',
      dataIndex: 'examCode',
      key: 'examCode',
      align: 'center',
      render: value => (
        <Radio
          checked={value === examCode}
          onChange={() => setExamCode(value)}
          text=""
        />
      ),
    },
    {
      title: t('program.semester.column24'),
      dataIndex: 'examDiv',
      key: 'examDiv',
      align: 'center',
      render: value => getCommonCodeLabel('TE0001', value, 'ko'),
    },
    {
      title: t('program.semester.column25'),
      dataIndex: 'examName',
      key: 'examName',
      align: 'center',
    },
    {
      title: t('program.semester.column11'),
      dataIndex: 'examTypeName',
      key: 'examTypeName',
      align: 'center',
    },
    {
      title: t('program.semester.column9'),
      dataIndex: 'year',
      key: 'year',
      align: 'center',
    },
    {
      title: t('program.semester.column22'),
      dataIndex: 'testSeq',
      key: 'testSeq',
      align: 'center',
    },
  ];

  return (
    <>
      {(isLoading || isLoading2 || submitIsLoading) && <Loader />}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={submitError} />

      <Modal
        title={t('program.semester.title7')}
        visible={visible}
        onCancel={onClose}
        width={750}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onSubmit}
            >
              {t('program.award.button2')}
            </AntButton>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={onClose}
            >
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        <div className="flex ">
          <div className="w-100 comm-table__area rowType mt-0">
            <Descriptions
              bordered
              size="small"
              column={3}
            >
              <Descriptions.Item label={t('program.semester.column32')}>
                <Select
                  className="w-100"
                  placeholder={t('common.button.select')}
                  value={examDiv}
                  onChange={value => setExamDiv(value)}
                >
                  <Select.Option value="">{t('common.text.all')}</Select.Option>
                  {
                    eduStepCode === 'EM'
                      ? <Select.Option value="MT">Monthly Test</Select.Option>
                      : <Select.Option value="PL">Prelim</Select.Option>
                  }
                  <Select.Option value="LT">Term Test</Select.Option>
                </Select>
              </Descriptions.Item>
              <Descriptions.Item label={t('program.semester.column9')}>
                <Select
                  className="w-100"
                  placeholder={t('common.button.select')}
                  value={year}
                  onChange={value => setYear(value)}
                >
                  <Select.Option value="">{t('common.text.all')}</Select.Option>
                  {getReverseYears(SY0005).map((item: any) => (
                    <Select.Option
                      key={item.detailCode}
                      value={item.detailCode}
                    >
                      {item.korName}
                    </Select.Option>
                  ))}
                </Select>
              </Descriptions.Item>
              <Descriptions.Item label={t('program.semester.column22')}>
                <Select
                  className="w-100"
                  placeholder={t('common.button.select')}
                  value={testSeq}
                  onChange={value => setTestSeq(value)}
                >
                  <Select.Option value="">{t('common.text.all')}</Select.Option>
                  {SY0011?.map((item) => (
                    <Select.Option
                      key={item.detailCode}
                      value={item.detailCode}
                    >
                      {item.korName}
                    </Select.Option>
                  ))}
                </Select>
              </Descriptions.Item>
              <Descriptions.Item label={t('program.semester.column33')}>
                <Select
                  className="w-100"
                  placeholder={t('common.button.select')}
                  value={examTypeName}
                  onChange={value => setExamTypeName(value)}
                  disabled={!examDiv}
                >
                  <Select.Option value="">{t('common.text.all')}</Select.Option>
                  {res?.data?.filter(item => item.eduStepCode === eduStepCode && item.examDiv === examDiv)?.map(item => (
                    <Select.Option
                      key={item.examTypeCode}
                      value={item.examTypeName}
                    >
                      {item.examTypeName}
                    </Select.Option>
                  ))}
                </Select>
              </Descriptions.Item>
              <Descriptions.Item label={t('program.semester.column25')} span={2}>
                <input
                  type="text"
                  className="form-control input-text"
                  value={examName}
                  onChange={e => setExamName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') onSearch();
                  }}
                />
              </Descriptions.Item>
            </Descriptions>
          </div>
          <div className="comm-btn__area w-100p pl-1">
            <Button
              className="btn-basic black btn-wh100"
              onClick={onSearch}
              text={t('common.button.search')}
            />
          </div>
        </div>
        {
          res2?.data?.length
            ? (
              <Table
                className="comm-table__area"
                columns={columns}
                dataSource={res2.data}
                pagination={{ pageSize: 9999999 }}
                rowKey="examCode"
                onRow={(record) => {
                  return {
                    onClick: () => {
                      setExamCode(record.examCode);
                    },
                  };
                }}                
              />
              )
            : (
                <TableEmpty text={t('program.semester.text9')} />
              )
        }
      </Modal>    
    </>
  );
}

export default SelectPaperModal;
